import get from 'lodash.get'

/**
 * Extracts pivot values for single grouping level
 * @param {Array} data - The hierarchical data array
 * @param {string} groupingLevel - Grouping level name (e.g., 'Registration')
 * @param {Function} getSubRows - Function to get sub-rows
 * @returns {Array} Array of pivot metadata objects
 */
export const extractPivotValuesByGroupingSingle = (data, groupingLevel, getSubRows) => {
  const valuesMap = new Map()

  const traverse = (rows) => {
    if (!Array.isArray(rows)) return

    rows.forEach(row => {
      if (row.grouping === groupingLevel) {
        const id = row.orionEntityId || row.levelName
        if (!valuesMap.has(id)) {
          valuesMap.set(id, {
            value: id,
            displayName: row.levelName,
            ordinal: row.ordinal
          })
        }
      }

      const subRows = getSubRows(row)
      if (subRows?.length) {
        traverse(subRows)
      }
    })
  }

  traverse(data)

  return Array.from(valuesMap.values()).sort((a, b) => {
    if (a.ordinal !== undefined && b.ordinal !== undefined) {
      return a.ordinal - b.ordinal
    }
    return String(a.displayName).localeCompare(String(b.displayName))
  })
}

/**
 * Extracts pivot values based on grouping field (no explicit ID fields needed)
 * @param {Array} data - The hierarchical data array
 * @param {string|Array} groupingLevels - Grouping level name(s)
 * @param {Function} getSubRows - Function to get sub-rows
 * @returns {Array} Array of pivot metadata or nested structure for column groups
 */
export const extractPivotValuesByGrouping = (data, groupingLevels, getSubRows) => {
  // Single level pivot
  if (!Array.isArray(groupingLevels)) {
    return extractPivotValuesByGroupingSingle(data, groupingLevels, getSubRows)
  }

  // Nested pivot (two levels)
  const [parentGrouping, childGrouping] = groupingLevels
  const parentValues = new Map()
  const childValues = new Map()

  const traverse = (rows) => {
    if (!Array.isArray(rows)) return

    rows.forEach(row => {
      const rowGrouping = row.grouping

      // Parent level
      if (rowGrouping === parentGrouping) {
        const parentId = row.orionEntityId || row.levelName
        if (!parentValues.has(parentId)) {
          parentValues.set(parentId, {
            value: parentId,
            displayName: row.levelName,
            ordinal: row.ordinal,
            children: []
          })
        }
      }

      // Child level
      if (rowGrouping === childGrouping) {
        const childId = row.orionEntityId || row.levelName
        const childKey = `${childId}`

        if (!childValues.has(childKey)) {
          const childMeta = {
            value: childId,
            displayName: row.levelName,
            ordinal: row.ordinal
          }
          childValues.set(childKey, childMeta)
        }
      }

      const subRows = getSubRows(row)
      if (subRows?.length) {
        traverse(subRows)
      }
    })
  }

  traverse(data)

  // Build parent-child relationships
  const childToParentMap = new Map()

  const findParentForChild = (rows, targetChildGrouping, targetParentGrouping) => {
    if (!Array.isArray(rows)) return

    rows.forEach(row => {
      if (row.grouping === targetParentGrouping) {
        const parentId = row.orionEntityId || row.levelName
        const subRows = getSubRows(row)
        if (subRows?.length) {
          subRows.forEach(child => {
            if (child.grouping === targetChildGrouping) {
              const childId = child.orionEntityId || child.levelName
              childToParentMap.set(childId, parentId)
            }
            findParentForChild([child], targetChildGrouping, targetParentGrouping)
          })
        }
      } else {
        const subRows = getSubRows(row)
        if (subRows?.length) {
          findParentForChild(subRows, targetChildGrouping, targetParentGrouping)
        }
      }
    })
  }

  findParentForChild(data, childGrouping, parentGrouping)

  // Assign children to parents
  childValues.forEach((childMeta, childKey) => {
    const parentId = childToParentMap.get(childMeta.value)
    if (parentId && parentValues.has(parentId)) {
      parentValues.get(parentId).children.push(childMeta)
    }
  })

  // Convert to array and sort
  const sorted = Array.from(parentValues.values())
    .sort((a, b) => {
      if (a.ordinal !== undefined && b.ordinal !== undefined) {
        return a.ordinal - b.ordinal
      }
      return String(a.displayName).localeCompare(String(b.displayName))
    })
    .map(parent => ({
      ...parent,
      children: parent.children.sort((a, b) => {
        if (a.ordinal !== undefined && b.ordinal !== undefined) {
          return a.ordinal - b.ordinal
        }
        return String(a.displayName).localeCompare(String(b.displayName))
      })
    }))

  return sorted
}

/**
 * Extracts nested pivot structure for column grouping (field-based)
 * @param {Array} data - The hierarchical data array
 * @param {Array} pivotAccessors - Array of pivot accessors [parent, child]
 * @param {Function} getSubRows - Function to get sub-rows
 * @returns {Array} Nested structure with parent groups containing children
 */
export const extractNestedPivotValues = (data, pivotAccessors, getSubRows) => {
  if (pivotAccessors.length < 2) {
    return extractPivotValues(data, pivotAccessors[0], getSubRows)
  }

  const [parentAccessor, childAccessor] = pivotAccessors
  const parentValues = new Map()
  const childValues = new Map()

  const traverse = (rows) => {
    if (!Array.isArray(rows)) return

    rows.forEach(row => {
      const parentValue = get(row, parentAccessor)
      const childValue = get(row, childAccessor)

      // Extract parent metadata
      if (parentValue !== undefined && parentValue !== null && !parentValues.has(parentValue)) {
        const baseAccessor = parentAccessor.replace(/Id$/, '').replace(/GroupId$/, '')
        parentValues.set(parentValue, {
          value: parentValue,
          displayName: get(row, `${baseAccessor}Name`) ||
                      get(row, `${baseAccessor}LongName`) ||
                      parentValue,
          ordinal: get(row, 'ordinal'),
          children: []
        })
      }

      // Extract child metadata and link to parent
      if (childValue !== undefined && childValue !== null) {
        const childKey = `${parentValue}|${childValue}`
        if (!childValues.has(childKey)) {
          const baseAccessor = childAccessor.replace(/Id$/, '').replace(/GroupId$/, '')
          const childMeta = {
            value: childValue,
            displayName: get(row, `${baseAccessor}Name`) ||
                        get(row, `${baseAccessor}LongName`) ||
                        childValue,
            ordinal: get(row, 'ordinal'),
            parentValue: parentValue
          }
          childValues.set(childKey, childMeta)

          // Add to parent's children
          if (parentValues.has(parentValue)) {
            parentValues.get(parentValue).children.push(childMeta)
          }
        }
      }

      const subRows = getSubRows(row)
      if (subRows?.length) {
        traverse(subRows)
      }
    })
  }

  traverse(data)

  // Sort parents and their children
  const sorted = Array.from(parentValues.values())
    .sort((a, b) => {
      if (a.ordinal !== undefined && b.ordinal !== undefined) {
        return a.ordinal - b.ordinal
      }
      return String(a.displayName).localeCompare(String(b.displayName))
    })
    .map(parent => ({
      ...parent,
      children: parent.children.sort((a, b) => {
        if (a.ordinal !== undefined && b.ordinal !== undefined) {
          return a.ordinal - b.ordinal
        }
        return String(a.displayName).localeCompare(String(b.displayName))
      })
    }))

  return sorted
}

/**
 * Extracts unique pivot values from the data with metadata (field-based)
 * @param {Array} data - The hierarchical data array
 * @param {string|Array} pivotAccessor - Path to the field to pivot on
 * @param {Function} getSubRows - Function to get sub-rows from a row
 * @returns {Array} Array of pivot metadata objects
 */
export const extractPivotValues = (data, pivotAccessor, getSubRows = (row) => row.data) => {
  // Handle array of pivot accessors (for nested column groups)
  if (Array.isArray(pivotAccessor)) {
    return extractNestedPivotValues(data, pivotAccessor, getSubRows)
  }

  const valuesMap = new Map()

  const traverse = (rows) => {
    if (!Array.isArray(rows)) return

    rows.forEach(row => {
      const value = get(row, pivotAccessor)
      if (value !== undefined && value !== null && !valuesMap.has(value)) {
        // Try to extract display name and ordinal
        const baseAccessor = pivotAccessor.replace(/Id$/, '').replace(/GroupId$/, '')
        const displayName = get(row, `${baseAccessor}Name`) ||
                          get(row, `${baseAccessor}LongName`) ||
                          get(row, 'levelName') ||
                          value
        const ordinal = get(row, 'ordinal')

        valuesMap.set(value, {
          value,
          displayName,
          ordinal
        })
      }

      const subRows = getSubRows(row)
      if (subRows?.length) {
        traverse(subRows)
      }
    })
  }

  traverse(data)

  // Sort by ordinal if available
  return Array.from(valuesMap.values()).sort((a, b) => {
    if (a.ordinal !== undefined && b.ordinal !== undefined) {
      return a.ordinal - b.ordinal
    }
    return String(a.displayName).localeCompare(String(b.displayName))
  })
}
